/**
 * Cliente HTTP único contra el backend (REACT_APP_SERVIDOR).
 *
 * Uso en componentes (inyecta las credenciales del usuario logueado):
 *
 *   import { useApi, useSucursales } from "lib/api";
 *
 *   const { post } = useApi();
 *   const { ok, status, data } = await post("/api/informespagos", { fechadesde, fechahasta });
 *
 *   const sucursales = useSucursales(); // [{ label: "..." }, ...]
 */
import { useState, useEffect, useCallback } from "react";
import { useMaterialUIController } from "context";

const BASE = process.env.REACT_APP_SERVIDOR || "";

// POST JSON crudo. Nunca lanza por respuesta no-2xx ni por body no-JSON:
// siempre resuelve a { ok, status, data } para que cada pantalla decida.
export async function apiPost(path, body = {}) {
  const res = await fetch(BASE + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// post() con usuario/password del contexto ya incluidos en el body.
export function useApi() {
  const [controller] = useMaterialUIController();
  const { usuarioLog, claveLog } = controller;

  const post = useCallback(
    (path, body = {}) => apiPost(path, { usuario: usuarioLog, password: claveLog, ...body }),
    [usuarioLog, claveLog]
  );

  return { post };
}

// Lista de sucursales al montar. `conTodos` antepone la opción "0-TODOS".
export function useSucursales({ conTodos = false } = {}) {
  const { post } = useApi();
  const [sucursales, setSucursales] = useState([]);

  useEffect(() => {
    let activo = true;
    post("/api/recuperasucursales").then(({ ok, data }) => {
      if (!activo || !ok) return;
      const lista = data.sucursales || [];
      setSucursales(conTodos ? [{ label: "0-TODOS" }, ...lista] : lista);
    });
    return () => {
      activo = false;
    };
  }, [post, conTodos]);

  return sucursales;
}
